import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpRequest, HttpEvent, HttpHeaders } from '@angular/common/http';
import { CookieService } from 'ngx-cookie';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpService } from './http.service';
import { Router } from '@angular/router';
import {
  ReportTemplate,
  ReportTemplateUpdate,
  ReportTemplateList,
  ResponseIdList,
  UpdateIdList, UpdateResultList, ReportGlobalImage, NewReportTemplateInfo
} from '../_objects/report-template';
import { TenantUserList } from '../_objects/user';
import { RtbAuthService } from '../auth/rtb-auth.service';

@Injectable()
export class ReportTemplateService extends HttpService {

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router,
    private rtbAuthService: RtbAuthService,
  ) {
    super(rtbAuthService);
    super.setCookieService(cookieService);
    super.setHttpClient(http);
    super.setRouter(router);
  }

  // TP01 GET /api/report/v1/{tenantId}/templates
  getReportTemplates(isVisol?: boolean, md?: string, enabledOnly?: boolean): Observable<any> {

    let httpParams = new HttpParams();
    if (isVisol != null && isVisol !== undefined) {
      httpParams = httpParams.set('isVisol', String(isVisol));
    }
    if (md) {
      httpParams = httpParams.set('md', md);
    }
    if (enabledOnly !== null && enabledOnly !== undefined) {
      httpParams = httpParams.set('enabledOnly', String(enabledOnly));
    }

    return this.http.get<ReportTemplateList>('api/ReportTemplates/getTemplates',
      { headers: this.getTimeZoneHeader(), withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map(response => response as ReportTemplateList),
        catchError((error, caught) => {
          this.handleErrorUnauthorized(error);
          throw (error);
        }));
  }

  // TP02 GET /api/report/v1/{tenantId}/templates/{templateId}
  getReportTemplate(reportTemplateId: string, isVisol: boolean): Observable<ReportTemplate> {

    let httpParams = new HttpParams();
    if (isVisol != null && isVisol !== undefined) {
      httpParams = httpParams.set('isVisol', String(isVisol));
    }

    return this.http.get<ReportTemplate>(`api/ReportTemplates/getTemplate/${reportTemplateId}`,
      { headers: this.getTimeZoneHeader(), withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map(response => response as ReportTemplate),
        catchError((error, caught) => {
          this.handleErrorUnauthorized(error);
          throw (error);
        }));
  }

  deleteReportTemplate(templateId: string, isVisol: boolean): Observable<any> {

    let httpParams = new HttpParams();
    if (isVisol != null && isVisol !== undefined) {
      httpParams = httpParams.set('isVisol', String(isVisol));
    }

    return this.http.delete(`api/ReportTemplates/deleteTemplate/${templateId}`,
      { headers: this.getTimeZoneHeader(), withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map(response => response),
        catchError((error, caught) => {
          this.handleErrorUnauthorized(error);
          throw (error);
        }));
  }

  // TP09 PUT /api/report/v1/{tenantId}/templates/{templateId}
  updateReportTemplate(reportTemplateUpdate: ReportTemplateUpdate, isVisol?: boolean): Observable<any> {

    let httpParams = new HttpParams();
    if (isVisol != null && isVisol !== undefined) {
      httpParams = httpParams.set('isVisol', String(isVisol));
    }

    return this.http.post<ReportTemplate>(`/api/ReportTemplates/saveTemplate`, reportTemplateUpdate,
      { headers: this.getTimeZoneHeader(), withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map(response => response),
        catchError((error, caught) => {
          this.handleErrorUnauthorized(error);
          throw (error);
        }));
  }
  // TP04 GET /api/report/v1/{tenantId}/templates/{templateId}/export
  exportReportTemplate(reportTemplateId: string, isVisol?: boolean): Observable<Blob> {
    let httpParams = new HttpParams();
    if (isVisol != null && isVisol !== undefined) {
      httpParams = httpParams.set('isVisol', String(isVisol));
    }
    return this.http.get(`api/templates/${reportTemplateId}/export`, {
      headers: this.getTimeZoneHeader(), withCredentials: super.getWithCredentialsOption(), params: httpParams, responseType: 'blob'
    }).pipe(map(response => response),
      catchError((error, caught) => {
        this.handleErrorUnauthorized(error);
        throw (error);
      }));
  }

  // TP05 POST /api/report/v1/{tenantId}/templates/import
  importReportTemplate(file: File, reportTemplate?: ReportTemplateList): Observable<HttpEvent<{}>> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    let httpParams = new HttpParams();
    if (reportTemplate) {
      httpParams = httpParams.set('templateId', reportTemplate.id);
    }

    const req = new HttpRequest('PUT', `api/templates/import`, formData,
      { headers: this.getTimeZoneHeader(), withCredentials: super.getWithCredentialsOption(), params: httpParams });
    return this.http.request(req).pipe(map(response => response),
      catchError((error, caught) => {
        this.handleErrorUnauthorized(error);
        throw (error);
      }));
  }

  importReportTemplateAsync(file: File): Observable<any> {

    let httpParams = new HttpParams();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filetype', file.type);
    formData.append('filename', file.name);
    formData.append('fileExtension', file.name.split('.')[1] ? file.name.split('.')[1] : '');

    const headers = new HttpHeaders();
    headers.append('Content-Type', undefined);

    return this.http.post<any>(`api/templates/importReportTemplate`, formData, { headers: headers, params: httpParams });

  }

  // TP06 GET /api/report/v1/{tenantId}/templates/models
  getModelsForTemplates(reportTemplateId?: string, subjectModelId?: string): Observable<ResponseIdList[]> {

    let httpParams = new HttpParams();
    if (reportTemplateId) {
      httpParams = httpParams.set('rt', reportTemplateId);
    }
    if (subjectModelId) {
      httpParams = httpParams.set('md', subjectModelId);
    }

    return this.http.get<ResponseIdList[]>('/api/templates/models',
      { headers: this.getTimeZoneHeader(), withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map(response => response as ResponseIdList[]),
        catchError((error, caught) => {
          this.handleErrorUnauthorized(error);
          throw (error);
        }));
  }

  // TP07 PUT /api/report/v1/{tenantId}/templates/{templateId}/models
  updateModelsForTemplates(templateId: string, updateModelIdList: UpdateIdList, isVisol: boolean): Observable<UpdateResultList> {
    if (!isVisol) {
      console.log('service');
      return of();
    }
    return this.http.put<UpdateResultList>(
      `/api/templates/${templateId}/models`,
      updateModelIdList,
      { headers: this.getTimeZoneHeader(), withCredentials: super.getWithCredentialsOption() }
    ).pipe(map(response => response as UpdateResultList),
      catchError((error, caught) => {
        this.handleErrorUnauthorized(error);
        throw (error);
      }));
  }
  // TP10 GET /api/report/v1/{tenantId}/templates/{templateId}/images
  getReportTemplateImages(templateId: string, isVisol: boolean): Observable<ReportGlobalImage[]> {

    let httpParams = new HttpParams();
    if (templateId) {
      httpParams = httpParams.set('templateId', templateId);
    }
    if (isVisol != null && isVisol !== undefined) {
      httpParams = httpParams.set('isVisol', String(isVisol));
    }

    return <Observable<ReportGlobalImage[]>>this.http.get('api/ReportTemplates/getImages',
      { headers: this.getTimeZoneHeader(), withCredentials: super.getWithCredentialsOption(), params: httpParams })
      .pipe(map(response => response as ReportGlobalImage[]),
        catchError((error, caught) => {
          this.handleErrorUnauthorized(error);
          throw (error);
        }));
  }

  public addReportTemplateImage(file: File, templateId: string, isVisol: boolean): Observable<any> {

    let httpParams = new HttpParams();
    if (templateId) {
      httpParams = httpParams.set('templateId', templateId);
    }
    if (isVisol != null && isVisol !== undefined) {
      httpParams = httpParams.set('isVisol', String(isVisol));
    }

    const formData = new FormData();

    formData.append('file', file);
    formData.append('filetype', file.type);
    formData.append('filename', file.name);
    formData.append('fileExtension', file.name.split('.')[1] ? file.name.split('.')[1] : '');

    const headers = new HttpHeaders();
    headers.append('Content-Type', undefined);

    return this.http.post<any>(`api/ReportTemplates/addImage`, formData, { headers: headers, params: httpParams });
  }

  // TP11 POST /api/report/v1/{tenantId}/templates/{templateId}/images
  updateReportTemplateImage(templateId: string, file: File): Observable<HttpEvent<{}>> {
    console.log('service');
    return of();
  }
  // TP12 DELETE /api/report/v1/{tenantId}/templates/{templateId}/images
  deleteReportTemplateImage(imageId: string, fileName: string, isVisol: boolean, templateId: string): Observable<any> {
    if (isVisol) {
      let headers: HttpHeaders = this.getTimeZoneHeader();
      headers = headers.append('Content-Type', 'application/json');
      headers = headers.append('responseType', 'json');
      return this.http.request('DELETE', `api/templates/${templateId}/images`, {
        'body': '"' + fileName + '"', 'responseType': 'json', headers,
        withCredentials: super.getWithCredentialsOption()
      }).pipe(map(response => response),
        catchError((error, caught) => {
          this.handleErrorUnauthorized(error);
          throw (error);
        }));
    }
    else {
      return this.http.delete(`api/ReportTemplates/deleteImage/${imageId}`);
    }
  }

  public getDynamicBindingsForIpm(): Observable<any> {
    return this.http.get('/api/ReportTemplates/getDynamicBindingsForIpm');
  }
  public createTemplate(templateName, isVisol: boolean, NewReport: NewReportTemplateInfo, templateId: string = null): Observable<any> {

    let httpParams = new HttpParams();
    if (isVisol != null && isVisol !== undefined) {
      httpParams = httpParams.set('isVisol', String(isVisol));
    }

    if (templateId != null) {
      httpParams = httpParams.set('templateId', templateId);
    }

    //return this.http.post(`/api/ReportTemplates/addTemplate/${templateName}`, NewReport);
    return this.http.post<ReportTemplate>(
      `/api/ReportTemplates/addTemplate/${templateName}`,
      NewReport,
      { headers: this.getTimeZoneHeader(), withCredentials: super.getWithCredentialsOption(), params: httpParams }
    ).pipe(map(response => response as ReportTemplate),
      catchError((error) => {
        this.handleErrorUnauthorized(error);
        throw (error);
      }));
  }

  public GetReportTypes(): Observable<any> {
    return this.http.get('/api/ReportTemplates/reporttypes');
  }

  public GetApiCodes(): Observable<any> {
    return this.http.get('/api/ReportTemplates/apicodes');
  }

  public GetEquipmentTypes(): Observable<any> {
    return this.http.get('/api/ReportTemplates/equipmenttypes');
  }

  public getStageNames(): Observable<any> {
    return this.http.get('/api/ReportTemplates/getStageNames');
  }

  public getUsers(): Observable<TenantUserList[]> {
    const endpoint = '/api/users';
    return this.http.get<TenantUserList[]>(endpoint);
  }
}
